import { createAiChatHttpClient } from './httpClient';
import { chatbotAgentsPath, chatbotAgentByIdPath } from './paths';
import { getAiChatApiBase } from '../config';
import { normalizeChatbotAgentRow, extractLakehouseFields } from './normalizeChatbotAgentRow';

/**
 * @param {unknown} err
 * @param {string} fallback
 * @returns {string}
 */
export function getAiChatAgentRequestErrorMessage(err, fallback) {
  const errMsg = err?.response?.data?.message || err?.message || fallback;
  return typeof errMsg === 'string' ? errMsg : String(errMsg);
}

/**
 * When DELETE /chatbot/agents/:id fails because the agent is in use, the API may return
 * `{ message, data: [{ projectName, moduleCount, modules: string[] }] }`.
 *
 * @param {unknown} err — axios error from deleteAgent
 * @returns {{ message: string, projects: Array<{ projectName: string, moduleCount: number, modules: string[] }> } | null}
 */
export function parseAgentDeleteBlockedByProjectsPayload(err) {
  const body = err?.response?.data;
  if (!body || typeof body !== 'object') {
    return null;
  }
  const { message, data } = body;
  if (!Array.isArray(data) || data.length === 0) {
    return null;
  }
  const projects = [];
  for (const row of data) {
    if (!row || typeof row !== 'object') {
      return null;
    }
    if (typeof row.projectName !== 'string') {
      return null;
    }
    if (!Array.isArray(row.modules)) {
      return null;
    }
    const modules = row.modules.map((m) => String(m));
    projects.push({
      projectName: row.projectName,
      moduleCount:
        typeof row.moduleCount === 'number' ? row.moduleCount : modules.length,
      modules,
    });
  }
  return {
    message:
      typeof message === 'string' && message.trim() !== ''
        ? message
        : 'Cannot delete this agent while it is in use.',
    projects,
  };
}

function parseAgentsListPayload(data) {
  if (Array.isArray(data)) {
    return data;
  }
  if (data && Array.isArray(data.data)) {
    return data.data;
  }
  return [];
}

/**
 * @returns {Promise<Array>}
 */
export async function listAgents() {
  if (!getAiChatApiBase()) {
    return [];
  }
  const client = createAiChatHttpClient();
  if (!client) {
    return [];
  }
  const { data } = await client.get(chatbotAgentsPath());
  return parseAgentsListPayload(data)
    .map((row) => normalizeChatbotAgentRow(row))
    .filter(Boolean);
}

/**
 * GET /chatbot/agents/:id — optional lakehouse fields when list response omits them.
 *
 * @param {string} id
 * @returns {Promise<object | null>}
 */
export async function getAgentById(id) {
  if (!getAiChatApiBase() || !id || typeof id !== 'string') {
    return null;
  }
  const client = createAiChatHttpClient();
  if (!client) {
    return null;
  }
  const { data } = await client.get(chatbotAgentByIdPath(id));
  let row =
    data && typeof data === 'object' && !Array.isArray(data)
      ? data
      : data && typeof data === 'object' && data.data
      ? data.data
      : null;
  if (!row || typeof row !== 'object') {
    return null;
  }
  const rootLh = extractLakehouseFields(row);
  const agentSub =
    row.agent && typeof row.agent === 'object' ? row.agent : null;
  if (agentSub) {
    const agentLh = extractLakehouseFields(agentSub);
    const merged = {
      ...agentSub,
      lakehouseName: rootLh.lakehouseName || agentLh.lakehouseName,
      lakehouseSQLEndpoint:
        rootLh.lakehouseSQLEndpoint || agentLh.lakehouseSQLEndpoint,
    };
    return normalizeChatbotAgentRow(merged);
  }
  return normalizeChatbotAgentRow(row);
}

/**
 * @param {{ name: string, displayName: string }} payload
 * @returns {Promise<object | null>}
 */
export async function createAgent(payload) {
  const client = createAiChatHttpClient();
  if (!client) {
    throw new Error('Chatbot API is not configured.');
  }
  const { data } = await client.post(chatbotAgentsPath(), payload);
  if (data && typeof data === 'object' && typeof data.id === 'string') {
    return normalizeChatbotAgentRow(data);
  }
  return null;
}

/**
 * @param {string} id
 * @param {{ name: string, displayName: string }} payload
 */
export async function updateAgent(id, payload) {
  const client = createAiChatHttpClient();
  if (!client) {
    throw new Error('Chatbot API is not configured.');
  }
  await client.put(chatbotAgentByIdPath(id), payload);
}

/**
 * @param {string} id
 */
export async function deleteAgent(id) {
  const client = createAiChatHttpClient();
  if (!client) {
    throw new Error('Chatbot API is not configured.');
  }
  await client.delete(chatbotAgentByIdPath(id));
}
